import React from 'react';
import type { ButtonV2Props } from '@withjoy/joykit';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestIdentity } from '@shared/utils/eventInfo/useGuestIdentity';
import { PeoplePickerButton } from '@shared/components/PeoplePicker';
import { ContactPickerButton } from './ContactPickerButton';
import type { HardcodedLabel, HardcodedLabelsMode } from '../PeoplePicker/usePeoplePickerButtonController';
import type { DeliveryMethodValue, PeoplePickerUseCase, RenderPickerTrigger } from '../PeoplePicker/PeoplePicker.types';
import type { OutsideScopeCopy } from '../PeoplePicker/PeoplePicker.types';

export type GuestPickerButtonProps = Readonly<
  Omit<ButtonV2Props, 'onClick' | 'children' | 'title' | 'itemType'> & {
    buttonTextDefault?: string;
    buttonTextHover?: string;
    title?: React.ReactNode;
    hardcodedLabels?: ReadonlyArray<HardcodedLabel>;
    hardcodedLabelsMode?: HardcodedLabelsMode;
    outsideScopeCopy?: OutsideScopeCopy;
    /** Copy for the guest list's select-all control in `requiredScope` mode. See PeoplePicker. */
    selectAllLabel?: string;
    onStartSelection?: () => void;
    useCase: PeoplePickerUseCase;
    // Forwarded to the underlying picker button to swap the default ButtonV2 for custom
    // trigger UI (e.g. recipient chips); the supplied onClick opens the picker.
    renderTrigger?: RenderPickerTrigger;
    headerBanner?: React.ReactNode;
  } & (
      | {
          itemType?: 'simple';
          enableSmsSelection?: never;
          smsOnly?: never;
          emailOnly?: never;
          initialDeliveryMethod?: never;
          allowUnreachableSelection?: never;
          preferenceStorageKey?: never;
          showDeliveryMethodSelector?: never;
          showPhoneChannel?: never;
        }
      | {
          itemType: 'withDeliveryMethod';
          enableSmsSelection: boolean;
          smsOnly?: boolean;
          emailOnly?: boolean;
          initialDeliveryMethod?: DeliveryMethodValue;
          allowUnreachableSelection?: boolean;
          preferenceStorageKey?: string;
          showDeliveryMethodSelector?: boolean;
          /** Show the WhatsApp or SMS channel per guest (MSG-584). Pass the `useIsSmsEnabled` result. */
          showPhoneChannel?: boolean;
        }
    )
>;

export const GuestPickerButton: React.FC<GuestPickerButtonProps> = props => {
  const { eventInfo, eventHandle = '' } = useEventInfo();
  // Shared with the draft and send paths so the three cannot disagree: the ContactPicker carries the
  // guestKey in `personIdTag`, and a consumer that still keyed off event type would read `id` — a
  // contact id — and label it a personId. See `resolveGuestIdentity` (MGT-69/MGT-70).
  const guestIdentity = useGuestIdentity();

  if (guestIdentity === 'guestKey' && eventInfo?.eventId) {
    return (
      <ContactPickerButton
        {...props}
        eventId={eventInfo.eventId}
      />
    );
  }

  // Anything other than a settled `personId` means the legacy picker is either wrong for this event
  // or not yet known to be right, and its roster is empty for a Firebase-free event — so a click
  // during that window opens a dialog showing zero guests. Disabled rather than swapped, so the
  // control doesn't move under the cursor once the identity settles.
  return (
    <PeoplePickerButton
      {...props}
      eventHandle={eventHandle}
      disabled={props.disabled || guestIdentity !== 'personId'}
    />
  );
};
